import React, { useState } from "react";

// Interactive bar chart. `data` = [{ key, label, value }]. Tapping a bar selects
// it (tooltip + caption with the value and MoM delta); the active bar glows orange,
// the others dim. Mirrors the design_agent chart_spec.
export default function RevenueChart({
  data = [],
  activeKey,
  onSelect,
  formatValue = (v) => String(v),
  brand = "#E05A2C",
  dim = "#3A322B",
  ink = "#FAF7F2",
  muted = "#857C72",
}) {
  const [hoverKey, setHoverKey] = useState(null);
  const vals = data.map((d) => (d.value == null ? 0 : d.value));
  const max = Math.max(1, ...vals);
  const activeIdx = data.findIndex((d) => d.key === activeKey);
  const active = data[activeIdx];
  const prev = activeIdx > 0 ? data[activeIdx - 1] : null;

  let caption = null;
  if (active) {
    let delta = "";
    if (prev && prev.value != null && prev.value !== 0 && active.value != null) {
      const ch = ((active.value - prev.value) / Math.abs(prev.value)) * 100;
      const sign = ch > 0 ? "+" : ch < 0 ? "−" : "";
      delta = ` · ${sign}${Math.abs(ch).toLocaleString("it-IT", { maximumFractionDigits: 1 })}% vs mese prec.`;
    }
    caption = `${active.label}: ${formatValue(active.value || 0)}${delta}`;
  }

  return (
    <div>
      <div className="flex items-end gap-2 md:gap-3 h-48">
        {data.map((d) => {
          const isActive = d.key === activeKey;
          const isHover = d.key === hoverKey;
          const h = d.value == null ? 0 : Math.max(4, (d.value / max) * 100);
          const on = isActive || isHover;
          const pending = !!d.pending;
          return (
            <button
              key={d.key}
              type="button"
              disabled={pending}
              onClick={() => !pending && onSelect && onSelect(d.key)}
              onMouseEnter={() => !pending && setHoverKey(d.key)}
              onMouseLeave={() => setHoverKey(null)}
              className="group relative flex-1 flex flex-col items-center justify-end h-full outline-none"
              aria-label={pending ? `${d.label}: in attesa` : `${d.label}: ${formatValue(d.value || 0)}`}
            >
              {on && !pending && (
                <div
                  className="absolute -top-1 translate-y-[-100%] whitespace-nowrap rounded-md px-2 py-1 text-[11px] font-mono"
                  style={{ background: "#1C1815", color: ink, border: `1px solid ${brand}` }}
                >
                  {formatValue(d.value || 0)}
                </div>
              )}
              {d.goalMet && (
                <div
                  className="mb-1 h-2 w-2 rounded-full shrink-0"
                  style={{ background: "#3FB863", boxShadow: "0 0 8px #3FB86399" }}
                  title="Obiettivo di fatturato raggiunto"
                  data-testid={`goal-met-${d.key}`}
                />
              )}
              <div
                className="w-full rounded-t-md transition-all duration-300"
                style={{
                  height: pending ? "6%" : `${h}%`,
                  background: pending
                    ? "repeating-linear-gradient(45deg, #241F1A, #241F1A 4px, #1C1815 4px, #1C1815 8px)"
                    : isActive
                    ? `linear-gradient(180deg, ${brand} 0%, #C0491F 100%)`
                    : isHover
                    ? "#5a4b40"
                    : dim,
                  boxShadow: isActive && !pending ? `0 0 22px ${brand}66` : "none",
                }}
              />
              <span
                className="mt-2 font-mono text-[10px] uppercase tracking-wide"
                style={{ color: pending ? "#4A423A" : isActive ? brand : muted }}
              >
                {d.label}
              </span>
            </button>
          );
        })}
      </div>
      {data.some((d) => d.goalPct != null) && (
        <div className="mt-2 flex items-start gap-2 md:gap-3" data-testid="goal-bars">
          {data.map((d) => (
            <div key={`gb-${d.key}`} className="flex-1 flex flex-col items-center">
              {d.goalPct != null ? (
                <>
                  <div className="h-1.5 w-full rounded-full overflow-hidden" style={{ background: dim }}>
                    <div
                      className="h-full rounded-full transition-all duration-500"
                      style={{ width: `${Math.min(100, d.goalPct)}%`, background: d.goalPct >= 100 ? "#3FB863" : brand }}
                    />
                  </div>
                  <span className="mt-1 font-mono text-[9px]" style={{ color: d.goalPct >= 100 ? "#3FB863" : muted }}>
                    {d.goalPct}%
                  </span>
                </>
              ) : (
                <span className="font-mono text-[9px]">&nbsp;</span>
              )}
            </div>
          ))}
        </div>
      )}
      {caption && (
        <p className="mt-4 text-sm" style={{ color: muted }}>
          <span style={{ color: ink }}>{caption}</span>
        </p>
      )}
    </div>
  );
}
